import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { User } from '../../store/authentication/model/user.model';
import { TableRequest } from '../../models/table/table-request.model';
import { TableResponse } from '../../models/table/table-response.model';
import { NewUser } from '../../models/user-management/new-user.model';
import { UserContact } from '../../models/user-management/user-contact.model';
import { UserPayment } from '../../models/user-management/user-payment.model';
import { TranslateService } from '@ngx-translate/core';
import { UserMessageDTO } from '../../models/user-management/user-message-dto.model';

@Injectable({
    providedIn: 'root'
})
export class UserManagementService {
    constructor(
        private http: HttpClient,
        private translate: TranslateService
    ) {}

    searchUserByName(search: string): Observable<User[]> {
        // check length of search string
        if (search.length > 0) {
            return this.http.get<User[]>(`${environment.host}/usermanagement/users/participantSearch/${search}`);
        } else {
            // if it's smaller than 2 send empty array to prevent autocomplete loader for endless spinning
            const users: User[] = [];
            return of(users);
        }
    }

    loadUsers(request: TableRequest): Observable<TableResponse<User>> {
        return this.http.post<TableResponse<User>>(`${environment.host}/usermanagement/load-users`, request);
    }

    getRoles(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.host}/usermanagement/roles`);
    }

    createUser(request: NewUser, isGuestAccount: boolean): Observable<User> {
        return this.http.post<User>(`${environment.host}/usermanagement/users?notifyUser=${request.notifyUser}&guest=${isGuestAccount}`, JSON.stringify(request));
    }

    deleteUsers(userIds: number[]): Observable<number> {
        return this.http.post<number>(`${environment.host}/usermanagement/users/bulk-delete`, JSON.stringify(userIds));
    }

    updateUser(user: NewUser, changePassword: boolean): Observable<User> {
        return this.http.put<User>(`${environment.host}/usermanagement/users/${user.id}?changePassword=${changePassword}&notifyUser=${user.notifyUser}`, JSON.stringify(user));
    }

    getUserContact(userID: number): Observable<UserContact> {
        return this.http.get<UserContact>(`${environment.host}/usermanagement/users/${userID}/contact`);
    }

    getUserPayment(userID: number): Observable<UserPayment> {
        return this.http.get<UserPayment>(`${environment.host}/usermanagement/users/${userID}/payment`);
    }

    translateRoles(roles: string | string[]): string {
        if (Array.isArray(roles)) {
            return roles
                .map((role) => this.translate.instant(`USERMANAGEMENT.MODAL_USER_ROLES_${role.toUpperCase()}`))
                .toString()
                .replaceAll(',', ', ');
        } else {
            return this.translate.instant(`USERMANAGEMENT.MODAL_USER_ROLES_${roles.toUpperCase()}`);
        }
    }

    sendMessageToUsers(message: UserMessageDTO): Observable<number> {
        return this.http.post<number>(`${environment.host}/usermanagement/message`, JSON.stringify(message));
    }
}
